import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { HiOutlineArrowNarrowLeft as BackIcon } from 'react-icons/hi';

import AssociatedApplicants from '../associatedApplicants';
import ChangeUsername from '../ChangeUsername';
import ChangePassword from '../changePassword';
import useUserSettings from './useUserSettings';
import RenderIf from '../../renderIf';
import { MuiStyledInput, MuiStyledSelect, MuiFooter, MuiModal } from '../../shared/muiControls';
import ShipyardMenu from '../../shared/shipyardMenu';

const HighlightedText = ({ children }) => {
    return (
        <Box
            component="span"
            sx={{ color: (theme) => theme.palette.primary.main }}
        >
            {children}
        </Box>
    );
};

const UserSettingsOther = () => {
    const { userId } = useParams();
    const {
        isDoingAjax,
        isInitialized,
        FirstName,
        LastName,
        PhoneNumber,
        EmailAddress,
        UserType,
        UserName,
        SchoolId,
        CreatedUTC,
        UpdatedUTC,
        Active,
        DynamicHeaderText,
        ClientOrgId,
        userSettings,
        userTypeOptions,
        schoolOptions,
        activeOptions,
        inError,
        normalizePhone,
        retrieveUserSettings,
        updateUserSettings,
        moveOwnership,
        goBack,
        newUserProcess,
        updateUserProcess,
        deleteUser,
    } = useUserSettings(userId);
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    React.useEffect(() => {
        retrieveUserSettings();
    }, []);

    return (
        <>
            <Box
                sx={{
                    padding: { xl: '1% 9.72% 7.5%', md: '1% 9.72% 10%' },
                }}
            >
                <Button onClick={goBack}>
                    <BackIcon /> Back to all
                </Button>
                <Typography
                    component="div"
                    sx={{
                        fontSize: '2rem',
                        fontWeight: 600,
                    }}
                >
                    <RenderIf isTrue={!isInitialized}>
                        Retrieving <HighlightedText>User Settings...</HighlightedText>
                    </RenderIf>

                    <RenderIf isTrue={isInitialized && parseInt(userId) > 0}>
                        Edit User <HighlightedText>{DynamicHeaderText}</HighlightedText>
                    </RenderIf>

                    <RenderIf isTrue={isInitialized && parseInt(userId) === 0}>
                        Add <HighlightedText>{DynamicHeaderText}</HighlightedText>
                    </RenderIf>
                </Typography>

                <RenderIf isTrue={isInitialized}>
                    <MuiModal open={isModalOpen === 'change_password'}>
                        <ChangePassword
                            callback={goBack}
                            userId={userId}
                            usingCurrentPassword
                            buttons={[
                                <Button
                                    onClick={() => setIsModalOpen(false)}
                                    variant="contained"
                                    key="cancel-button"
                                    color="greyButton"
                                >
                                    Cancel
                                </Button>,
                            ]}
                        />
                    </MuiModal>

                    <MuiModal open={isModalOpen === 'change_username'}>
                        <ChangeUsername
                            callback={goBack}
                            userId={userId}
                            buttons={[
                                <Button
                                    onClick={() => setIsModalOpen(false)}
                                    variant="contained"
                                    key="cancel-button"
                                    color="greyButton"
                                >
                                    Cancel
                                </Button>,
                            ]}
                        />
                    </MuiModal>

                    <Paper
                        sx={{
                            padding: (theme) => theme.spacing(2.5),
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 700,
                                color: (theme) => theme.palette.primary.main,
                                marginBottom: '1rem',
                                fontSize: '1.125rem',
                            }}
                        >
                            User Information
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                marginBottom: '4rem',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: (theme) => theme.spacing(5.15),
                                }}
                            >
                                <MuiStyledInput
                                    id="cognito-first-name"
                                    label="First Name:"
                                    value={FirstName}
                                    onChange={(event) => updateUserSettings('FirstName', event.target.value)}
                                    disabled={isDoingAjax}
                                    error={inError.find((el) => el?.field === 'FirstName')}
                                    errorMessage={inError.find((el) => el?.field === 'FirstName')?.message}
                                    inputProps={{
                                        tabIndex: '1',
                                    }}
                                />

                                <MuiStyledSelect
                                    id="user-type"
                                    label="User Type:"
                                    value={UserType}
                                    onChange={(event) => updateUserSettings('UserType', event.target.value)}
                                    error={inError.find((el) => el?.field === 'UserType')}
                                    errorMessage={inError.find((el) => el?.field === 'UserType')?.message}
                                    disabled={(userSettings?.UserType < 4 && parseInt(userId) !== 0) || isDoingAjax}
                                    inputProps={{
                                        tabIndex: '3',
                                    }}
                                >
                                    {userTypeOptions}
                                </MuiStyledSelect>

                                <MuiStyledInput
                                    id="cognito-phone-number"
                                    label="Phone Number:"
                                    value={normalizePhone(PhoneNumber)}
                                    onChange={(event) => updateUserSettings('PhoneNumber', event.target.value)}
                                    disabled={isDoingAjax}
                                    error={inError.find((el) => el?.field === 'PhoneNumber')}
                                    errorMessage={inError.find((el) => el?.field === 'PhoneNumber')?.message}
                                    inputProps={{
                                        tabIndex: '6',
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: (theme) => theme.spacing(5.15),
                                }}
                            >
                                <MuiStyledInput
                                    id="cognito-last-name"
                                    label="Last Name:"
                                    value={LastName}
                                    onChange={(event) => updateUserSettings('LastName', event.target.value)}
                                    disabled={isDoingAjax}
                                    error={inError.find((el) => el?.field === 'LastName')}
                                    errorMessage={inError.find((el) => el?.field === 'LastName')?.message}
                                    inputProps={{
                                        tabIndex: '2',
                                    }}
                                />

                                <RenderIf isTrue={UserType === 0 || UserType === 1}>
                                    <MuiStyledInput
                                        label="Organization:"
                                        value="N/A"
                                        disabled
                                        inputProps={{
                                            tabIndex: '4',
                                        }}
                                    />
                                </RenderIf>

                                <RenderIf isTrue={UserType === 2}>
                                    <MuiStyledSelect
                                        id="user-org"
                                        label="Organization:"
                                        value={SchoolId}
                                        onChange={(event) => updateUserSettings('SchoolId', event.target.value)}
                                        error={inError.find((el) => el?.field === 'SchoolId')}
                                        errorMessage={inError.find((el) => el?.field === 'SchoolId')?.message}
                                        inputProps={{
                                            tabIndex: '4',
                                        }}
                                        disabled={isDoingAjax}
                                    >
                                        {schoolOptions}
                                    </MuiStyledSelect>
                                </RenderIf>

                                <RenderIf isTrue={UserType === 3}>
                                    <MuiStyledInput
                                        label="Organization:"
                                        value="OK SDE"
                                        disabled
                                        inputProps={{
                                            tabIndex: '4',
                                        }}
                                    />
                                </RenderIf>

                                <RenderIf isTrue={UserType === 4}>
                                    <MuiStyledInput
                                        label="Organization:"
                                        value="Lindsey Nicole Henry Scholarship Program"
                                        disabled
                                        inputProps={{
                                            tabIndex: '4',
                                        }}
                                    />
                                </RenderIf>

                                <MuiStyledInput
                                    id="contact-email"
                                    label="Email Address:"
                                    value={EmailAddress}
                                    onChange={(event) => updateUserSettings('EmailAddress', event.target.value)}
                                    disabled={isDoingAjax}
                                    textFieldSxObj={(theme) => ({
                                        width: '22.5rem',
                                        [theme.breakpoints.down('xl')]: {
                                            width: '17.5rem',
                                        },
                                        [theme.breakpoints.down('lg')]: {
                                            width: '12.5rem',
                                        },
                                    })}
                                    error={inError.find((el) => el?.field === 'EmailAddress')}
                                    errorMessage={inError.find((el) => el?.field === 'EmailAddress')?.message}
                                    inputProps={{
                                        tabIndex: '7',
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: (theme) => theme.spacing(5.15),
                                }}
                            >
                                <MuiStyledInput
                                    id="created-date"
                                    label="Created Date:"
                                    value={CreatedUTC}
                                    disabled
                                />

                                <MuiStyledSelect
                                    label="Language Preference:"
                                    id="lang-pref"
                                    value={'en-US'}
                                    disabled
                                >
                                    {[
                                        {
                                            id: 'en-US',
                                            label: 'English',
                                        },
                                    ]}
                                </MuiStyledSelect>

                                <MuiStyledInput
                                    id="cognito-username"
                                    label="Username:"
                                    value={UserName}
                                    textFieldSxObj={(theme) => ({
                                        width: '22.5rem',
                                        [theme.breakpoints.down('xl')]: {
                                            width: '17.5rem',
                                        },
                                        [theme.breakpoints.down('lg')]: {
                                            width: '12.5rem',
                                        },
                                    })}
                                    disabled
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: (theme) => theme.spacing(5.15),
                                }}
                            >
                                <MuiStyledInput
                                    id="last-updated-date"
                                    label="Last Updated Date:"
                                    value={UpdatedUTC}
                                    disabled
                                />

                                <MuiStyledSelect
                                    label="Active:"
                                    id="is-active"
                                    value={Active}
                                    onChange={(event) => {
                                        updateUserSettings('Active', event.target.value);
                                    }}
                                    inputProps={{
                                        tabIndex: '5',
                                    }}
                                    disabled={isDoingAjax}
                                >
                                    {activeOptions}
                                </MuiStyledSelect>

                                <MuiStyledInput
                                    id="cognito-password"
                                    label="Password:"
                                    value="********"
                                    disabled
                                />
                            </Box>
                        </Box>

                        <RenderIf isTrue={isInitialized && parseInt(userId) > 0 && ClientOrgId === 5 && UserType === 1}>
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    color: (theme) => theme.palette.primary.main,
                                    marginBottom: '1rem',
                                    fontSize: '1.125rem',
                                }}
                            >
                                Student Associations
                            </Typography>
                            <Box>
                                <AssociatedApplicants UserID={userId} />
                            </Box>
                        </RenderIf>
                    </Paper>
                </RenderIf>
            </Box>

            <MuiFooter
                rightContent={[
                    <RenderIf
                        isTrue={parseInt(userId) !== 0 && userSettings.ClientOrgId !== 7}
                        key="actions-menu"
                    >
                        <ShipyardMenu
                            label="Select Actions"
                            actions={[
                                {
                                    label: 'Move ownership',
                                    action: moveOwnership,
                                    disabled:
                                        parseInt(userId) === 0 ||
                                        userSettings.ClientOrgId === 7 ||
                                        UserType !== 1 ||
                                        userSettings.UserType === 4,
                                },
                                {
                                    label: 'Change username',
                                    action: () => setIsModalOpen('change_username'),
                                    disabled: parseInt(userId) === 0 || userSettings.ClientOrgId === 7 || userSettings.UserType < 3,
                                },
                                {
                                    label: 'Change password',
                                    action: () => setIsModalOpen('change_password'),
                                    disabled: parseInt(userId) === 0 || userSettings.ClientOrgId === 7 || userSettings.UserType < 3,
                                },
                            ]}
                            icon={<FontAwesomeIcon icon={faAngleDown} />}
                            disabled={isDoingAjax}
                            ButtonProps={{
                                tabIndex: 9,
                            }}
                        />
                    </RenderIf>,
                    <Button
                        onClick={() => (parseInt(userId) === 0 ? newUserProcess() : updateUserProcess())}
                        variant="contained"
                        disabled={isDoingAjax}
                        tabIndex={10}
                        key="save-button"
                    >
                        Save Changes
                    </Button>,
                ]}
                leftContent={[
                    <Button
                        variant="contained"
                        color="error"
                        disabled={isDoingAjax}
                        onClick={() => deleteUser()}
                        tabIndex={8}
                        key="delete-button"
                    >
                        Delete User
                    </Button>,
                ]}
            />
        </>
    );
};

export default UserSettingsOther;
